// 90 cards + 2 follow-ups. Each card:
//   { id, role, name, title, prompt, left, right, triggers? }
// Choice deltas use single-letter keys: e(ng), x(ec), u(ser impact), p(ersonal energy).
// `triggers: "id"` schedules a follow-up to surface ~3 turns later.
// Follow-up cards have `followup: true` so they aren't pulled in the main shuffle.

export const CARDS = [
  // ── Week 1: orientation ──────────────────────────────────────────────
  {
    id: 'c001', role: 'engineer', name: 'Priya', title: 'Staff Engineer',
    prompt: 'The auth migration is going to slip two weeks. Do we tell the exec team now, or wait for the demo Friday?',
    left:  { label: 'Wait for the demo', e: +5, x: -15, u: -5, p: 0 },
    right: { label: 'Flag it today',     e: -5, x: +10, u:  0, p: -5 },
  },
  {
    id: 'c002', role: 'designer', name: 'Rita', title: 'Senior Designer',
    prompt: 'Engineering shipped the new dashboard without my redesigned empty state. Again.',
    left:  { label: 'Push a hotfix',   e: -10, x:  0, u: +10, p: -5 },
    right: { label: 'Fold it into v2', e:  +5, x:  0, u:  -5, p:  0 },
  },
  {
    id: 'c003', role: 'exec', name: 'Karen', title: 'VP Product',
    prompt: 'Board wants an "AI strategy" slide for Thursday. We don\'t have an AI strategy.',
    left:  { label: 'Sketch one over the weekend', e:  0, x: +15, u:  0, p: -15 },
    right: { label: "Tell her it's premature",     e: +5, x: -10, u:  0, p:   0 },
  },
  {
    id: 'c004', role: 'customer', name: 'Eve', title: 'PM at TrialCo',
    prompt: '"Your onboarding made me cry. Not in a good way. We churned to your competitor."',
    left:  { label: 'Schedule a call',    e:  0, x:  0, u: +10, p: -5 },
    right: { label: 'Forward to support', e:  0, x: +5, u: -10, p:  0 },
  },
  {
    id: 'c005', role: 'sales', name: 'Jules', title: 'Enterprise AE',
    prompt: 'Strategic prospect wants SSO + audit logs + a custom SLA. €500k ARR on the line.',
    left:  { label: 'Decline politely', e: +10, x: -10, u:  +5, p:  0 },
    right: { label: 'Build it',         e: -15, x: +10, u: -10, p: -5,
             triggers: 'f_custom_arr' },
  },
  {
    id: 'c006', role: 'data', name: 'Ash', title: 'Data Scientist',
    prompt: "The A/B test is inconclusive but we're past the deadline. Ship the variant or kill it?",
    left:  { label: 'Ship the variant',   e:  0, x: +5, u:  -5, p:  0 },
    right: { label: 'Kill it, run again', e: +5, x: -5, u:  +5, p: -5 },
  },
  {
    id: 'c007', role: 'intern', name: 'Wren', title: 'Product Intern',
    prompt: '"I have an idea for a new feature. It\'s basically Slack but for cats."',
    left:  { label: 'Tell them to focus', e:  0, x:  0, u:  0, p: +5 },
    right: { label: 'Hear them out',      e: +5, x: -5, u: +5, p: -5 },
  },
  {
    id: 'c008', role: 'recruiter', name: 'Sam', title: 'Tech Recruiter',
    prompt: 'The senior PM candidate just got a counter from Stripe. We have 24 hours.',
    left:  { label: 'Match the offer', e:  0, x: -10, u:  0, p: -5 },
    right: { label: 'Let them walk',   e: -5, x:   0, u: -5, p:  0 },
  },
  {
    id: 'c009', role: 'engineer', name: 'Marco', title: 'Engineering Manager',
    prompt: 'Half my team thinks the roadmap is performance work. The other half thinks it\'s the redesign.',
    left:  { label: 'Clarify in a doc',    e: +10, x:  0, u:  0, p: -10 },
    right: { label: 'Tell them to ask EM', e:  -5, x:  0, u:  0, p:  +5 },
  },
  {
    id: 'c010', role: 'designer', name: 'Theo', title: 'Product Designer',
    prompt: 'Marketing pushed a Figma to me with their idea for the pricing page. It uses Comic Sans.',
    left:  { label: 'Politely redo it',      e:  0, x:  -5, u: +5, p: -5 },
    right: { label: 'Forward to brand team', e:  0, x:   0, u:  0, p:  0 },
  },

  // ── Week 2: settling in ──────────────────────────────────────────────
  {
    id: 'c011', role: 'exec', name: 'Tom', title: 'CEO',
    prompt: '"Why are we still on quarterly OKRs? Anthropic ships every six weeks."',
    left:  { label: 'Pilot 6-week cycles', e: -5, x: +10, u:  0, p: -10 },
    right: { label: 'Defend the quarter',  e: +5, x:  -5, u:  0, p:   0 },
  },
  {
    id: 'c012', role: 'customer', name: 'BigCo IT', title: 'Procurement',
    prompt: "They need a SOC 2 Type II report by month-end or they're not renewing.",
    left:  { label: 'Promise the date',     e: -10, x:   0, u:  0, p: -5 },
    right: { label: 'Push to next quarter', e:   0, x: -10, u: -5, p:  0 },
  },
  {
    id: 'c013', role: 'sales', name: 'Gabe', title: 'Account Executive',
    prompt: 'Demo prospect asked if we have an integration with HubSpot. We don\'t. Can I tell them "on the roadmap"?',
    left:  { label: 'Yes, soft-promise it', e: -5, x: +5, u:  0, p:  0 },
    right: { label: 'No, tell them no',     e: +5, x: -5, u:  0, p: -5 },
  },
  {
    id: 'c014', role: 'data', name: 'Lin', title: 'Senior Data Scientist',
    prompt: 'I think the activation funnel is double-counting trials from the same email domain.',
    left:  { label: 'Pause the metric',  e:  0, x: -10, u: +5, p: -5 },
    right: { label: 'Fix quietly later', e:  0, x:   0, u: -5, p:  0 },
  },
  {
    id: 'c015', role: 'intern', name: 'Wren', title: 'Product Intern',
    prompt: '"Can I sit in on the leadership offsite? I\'d just take notes."',
    left:  { label: 'Bring them along', e:  0, x: -5, u: +5, p:  0 },
    right: { label: 'Maybe next time',  e:  0, x:  0, u:  0, p:  0 },
  },
  {
    id: 'c016', role: 'engineer', name: 'Diego', title: 'Senior Engineer',
    prompt: "There's six months of design debt in the settings page. Want me to fix it during my 20% time?",
    left:  { label: 'Yes please',      e: +10, x: -5, u: +5, p:  0 },
    right: { label: 'Stay on roadmap', e:  -5, x: +5, u: -5, p:  0 },
  },
  {
    id: 'c017', role: 'designer', name: 'Nina', title: 'UX Researcher',
    prompt: 'I have five user interviews scheduled. Want to join one to see how it goes?',
    left:  { label: 'Block 2 hours, join', e:  0, x: -5, u: +10, p: -5 },
    right: { label: 'Just send the notes', e:  0, x:  0, u:  -5, p:  0 },
  },
  {
    id: 'c018', role: 'exec', name: 'Marcus', title: 'CRO',
    prompt: "Sales says we'd close 30% more if we had a free tier. Marketing says it'd kill conversion.",
    left:  { label: 'Run a free-tier pilot', e: -5, x: +5, u: +10, p: -10 },
    right: { label: 'Hold the line',         e: +5, x: -5, u:  -5, p:   0 },
  },
  {
    id: 'c019', role: 'customer', name: 'Eve', title: 'Power User, ScaleCo',
    prompt: '"Your CSV export truncates at 10k rows and we need 100k. This is blocking our quarter."',
    left:  { label: 'Ship a higher limit', e: -5, x:  0, u: +10, p: -5 },
    right: { label: 'Suggest the API',     e:  0, x:  0, u:  -5, p:  0 },
  },
  {
    id: 'c020', role: 'sales', name: 'Jules', title: 'Enterprise AE',
    prompt: 'Their CTO wants a 30-min call with "the PM who actually owns the roadmap". That\'s you.',
    left:  { label: 'Take the call',       e:  0, x: +5, u: +5, p: -10 },
    right: { label: 'Send a deck instead', e:  0, x: -5, u:  0, p:   0 },
  },

  // ── Week 3-4: shipping ───────────────────────────────────────────────
  {
    id: 'c021', role: 'engineer', name: 'Yusuf', title: 'Frontend Lead',
    prompt: 'Should we migrate the design system to v3 before or after the redesign launch?',
    left:  { label: 'Before (do it right)', e: +10, x: -5, u: -5, p: -10 },
    right: { label: 'After (ship first)',   e:  -5, x: +5, u: +5, p:   0 },
  },
  {
    id: 'c022', role: 'data', name: 'Ash', title: 'Data Scientist',
    prompt: 'Mixpanel says retention is up 4%. Amplitude says it\'s flat. Both are wired by interns.',
    left:  { label: 'Audit both',           e:  +5, x: -5, u: +5, p: -10 },
    right: { label: 'Cite Mixpanel anyway', e:   0, x: +5, u: -5, p:  +5 },
  },
  {
    id: 'c023', role: 'exec', name: 'Karen', title: 'VP Product',
    prompt: 'Can you write up a one-pager on "product-led growth" for Friday? Just three slides.',
    left:  { label: 'Make it a real one',     e:  0, x: +10, u:  0, p: -10 },
    right: { label: "Repurpose Lenny's post", e:  0, x:   0, u:  0, p:  -5 },
  },
  {
    id: 'c024', role: 'intern', name: 'Wren', title: 'Product Intern',
    prompt: 'I noticed the activation email goes out 14 minutes after signup. Should it be instant?',
    left:  { label: 'Get it fixed today', e:  0, x:  0, u: +10, p: -5 },
    right: { label: 'File a ticket',      e:  0, x:  0, u:   0, p:  0 },
  },
  {
    id: 'c025', role: 'recruiter', name: 'Sam', title: 'Tech Recruiter',
    prompt: 'We have headcount for one more PM. Senior generalist or junior with growth chops?',
    left:  { label: 'Senior generalist',     e: +5, x: +5, u:  0, p: +5 },
    right: { label: 'Growth-focused junior', e:  0, x:  0, u: +10, p: 0 },
  },
  {
    id: 'c026', role: 'designer', name: 'Rita', title: 'Senior Designer',
    prompt: 'Should we A/B test the new pricing page or just ship it?',
    left:  { label: 'A/B test it',  e:  0, x: -5, u: +5, p: -5 },
    right: { label: 'Just ship it', e:  0, x: +5, u: -5, p:  0 },
  },
  {
    id: 'c027', role: 'engineer', name: 'Priya', title: 'Staff Engineer',
    prompt: "We have a P1 bug in prod. The on-call eng is at her sister's wedding.",
    left:  { label: 'Page someone else',   e:  -5, x:   0, u:  +5, p: -5 },
    right: { label: 'Wait until tomorrow', e:   0, x: -10, u: -10, p:  0 },
  },
  {
    id: 'c028', role: 'customer', name: 'BigCo IT', title: 'Security Team',
    prompt: '"Your data residency story is unclear. We need EU-only by Q3 or we can\'t expand."',
    left:  { label: 'Commit to Q3',  e: -10, x: +10, u: +5, p: -5 },
    right: { label: 'Push it to Q4', e:   0, x:  -5, u: -5, p:  0 },
  },
  {
    id: 'c029', role: 'sales', name: 'Gabe', title: 'Account Executive',
    prompt: "I told a prospect we'd build a Salesforce sync. Don't worry, it's small.",
    left:  { label: 'Add it to the roadmap', e: -10, x:  0, u:  0, p: -5,
             triggers: 'f_salesforce_sync' },
    right: { label: 'Make him retract it',   e:  +5, x: -5, u:  0, p: -5 },
  },
  {
    id: 'c030', role: 'data', name: 'Lin', title: 'Senior Data Scientist',
    prompt: 'The North Star metric is "weekly active teams". Half the company tracks "daily active users".',
    left:  { label: 'Force alignment',  e:  0, x: +5, u:  0, p: -10 },
    right: { label: 'Let people drift', e:  0, x: -5, u: -5, p:   0 },
  },

  // ── Day 30-45 ────────────────────────────────────────────────────────
  {
    id: 'c031', role: 'exec', name: 'Sandra', title: 'CFO',
    prompt: 'Cloud bill went up 38% last month. Was that you?',
    left:  { label: 'Investigate',      e: +5, x:  +5, u:  0, p: -10 },
    right: { label: 'Blame the vendor', e: -5, x: -10, u:  0, p:   5 },
  },
  {
    id: 'c032', role: 'designer', name: 'Theo', title: 'Product Designer',
    prompt: 'I want to do an offsite design sprint. Three days, no meetings. Approved?',
    left:  { label: 'Approved',           e: +5, x: -5, u: +5, p:  0 },
    right: { label: 'Maybe next quarter', e: -5, x:  0, u:  0, p:  0 },
  },
  {
    id: 'c033', role: 'engineer', name: 'Marco', title: 'Engineering Manager',
    prompt: "My team wants to rewrite the billing service in Rust. They're already prototyping.",
    left:  { label: 'Stop the prototype', e: -10, x: +5, u:  0, p:  0 },
    right: { label: 'Time-box one week',  e:   0, x:  0, u:  0, p: -5 },
  },
  {
    id: 'c034', role: 'customer', name: 'Eve', title: 'Power User, ScaleCo',
    prompt: '"You promised dark mode at the QBR. It\'s been six months."',
    left:  { label: 'Ship dark mode now', e: -10, x:  0, u: +15, p: -10 },
    right: { label: 'Apologize, rephase', e:   0, x:  0, u: -10, p:   0 },
  },
  {
    id: 'c035', role: 'sales', name: 'Jules', title: 'Enterprise AE',
    prompt: 'Can I share the Notion roadmap with the prospect? They keep asking.',
    left:  { label: 'Send a sanitized PDF', e:  0, x: +5, u:  0, p: -5 },
    right: { label: 'Send the live link',   e:  0, x: -5, u:  0, p:  0 },
  },
  {
    id: 'c036', role: 'data', name: 'Ash', title: 'Data Scientist',
    prompt: 'I built a churn model. It says our biggest customer will leave in 47 days.',
    left:  { label: 'Tell their CSM',       e:  0, x:  0, u: +10, p: -5 },
    right: { label: 'Wait for more signal', e:  0, x: -5, u:  -5, p:  0 },
  },
  {
    id: 'c037', role: 'intern', name: 'Wren', title: 'Product Intern',
    prompt: '"My internship ends in two weeks. Any chance of a return offer?"',
    left:  { label: "Yes, I'll fight for it", e: +5, x: -5, u: +5, p: -5 },
    right: { label: 'Be honest: not sure',    e:  0, x:  0, u:  0, p:  0 },
  },
  {
    id: 'c038', role: 'recruiter', name: 'Sam', title: 'Tech Recruiter',
    prompt: 'The candidate ghosted us after the take-home. Can we waive it for senior folks?',
    left:  { label: 'Waive for seniors', e: +5, x:  0, u:  0, p: +5 },
    right: { label: 'Keep the bar',      e: -5, x:  0, u:  0, p: -5 },
  },
  {
    id: 'c039', role: 'exec', name: 'Tom', title: 'CEO',
    prompt: 'I want a demo at All Hands on Friday. Pick something cool.',
    left:  { label: 'Demo the half-built AI feature', e: -5, x: +10, u:  0, p: -10 },
    right: { label: 'Demo the boring billing fix',    e: +5, x:  -5, u: +5, p:   0 },
  },
  {
    id: 'c040', role: 'engineer', name: 'Diego', title: 'Senior Engineer',
    prompt: "Our staging environment hasn't worked in three weeks. Nobody owns it.",
    left:  { label: 'Take ownership of it', e: +10, x:  0, u:  0, p: -10 },
    right: { label: "It's an SRE problem",  e:  -5, x:  0, u: -5, p:   0 },
  },

  // ── Day 45-60 ────────────────────────────────────────────────────────
  {
    id: 'c041', role: 'designer', name: 'Rita', title: 'Senior Designer',
    prompt: 'Brand wants to refresh our logo. They have a moodboard with seven serifs on it.',
    left:  { label: 'Defer to brand',   e:  0, x:  0, u:  0, p: +5 },
    right: { label: 'Ask them to wait', e:  0, x: -5, u:  0, p: -5 },
  },
  {
    id: 'c042', role: 'customer', name: 'BigCo IT', title: 'Admin',
    prompt: '"Your latest release silently broke our SAML setup. We\'re back on email/password."',
    left:  { label: 'Roll back tonight',    e: -5, x: +5, u: +10, p: -10 },
    right: { label: 'Patch in the morning', e:  0, x: -5, u: -5,  p:   0 },
  },
  {
    id: 'c043', role: 'sales', name: 'Gabe', title: 'Account Executive',
    prompt: 'Prospect\'s CTO wants to see our actual Jira board. "For trust".',
    left:  { label: 'Share a sanitized view', e:  0, x: +5, u:  0, p: -5 },
    right: { label: "No, that's weird",       e:  0, x: -5, u:  0, p:  0 },
  },
  {
    id: 'c044', role: 'data', name: 'Lin', title: 'Senior Data Scientist',
    prompt: 'Looker Studio went down. Two execs are pinging me. Can I just send them yesterday\'s CSV?',
    left:  { label: 'Send the CSV',   e:  0, x: +5, u:  0, p:  0 },
    right: { label: 'Make them wait', e:  0, x: -5, u:  0, p:  0 },
  },
  {
    id: 'c045', role: 'exec', name: 'Karen', title: 'VP Product',
    prompt: "I'm putting you up to present at the leadership offsite. 20 minutes, no slides.",
    left:  { label: 'Take the slot',    e:  0, x: +15, u:  0, p: -15 },
    right: { label: 'Decline politely', e:  0, x: -10, u:  0, p:  +5 },
  },
  {
    id: 'c046', role: 'engineer', name: 'Yusuf', title: 'Frontend Lead',
    prompt: 'Should we adopt React 19 now or wait six months for the dust to settle?',
    left:  { label: 'Adopt now',       e:  +5, x:  0, u:  0, p: -5 },
    right: { label: 'Wait six months', e:  -5, x:  0, u:  0, p:  0 },
  },
  {
    id: 'c047', role: 'intern', name: 'Wren', title: 'Product Intern',
    prompt: '"I shadowed three sales calls and made a doc on top objections. Can we ship a fix?"',
    left:  { label: 'Bring it to standup',   e: +5, x: +5, u: +10, p: -5 },
    right: { label: 'Save for next quarter', e:  0, x:  0, u:  -5, p:  0 },
  },
  {
    id: 'c048', role: 'recruiter', name: 'Sam', title: 'Tech Recruiter',
    prompt: 'The candidate said they want to "work on something that matters". Should I send the values deck?',
    left:  { label: 'Send the deck',  e:  0, x:  0, u:  0, p:  0 },
    right: { label: 'Just be honest', e: +5, x:  0, u: +5, p: -5 },
  },
  {
    id: 'c049', role: 'customer', name: 'Eve', title: 'Power User',
    prompt: '"I\'d pay double if you let me self-host. I trust nobody with my data, no offense."',
    left:  { label: 'Explore self-hosted tier', e: -5, x: +5, u: +10, p: -10 },
    right: { label: 'Politely decline',         e:  0, x:  0, u:  -5, p:   0 },
  },
  {
    id: 'c050', role: 'exec', name: 'Marcus', title: 'CRO',
    prompt: 'Can we pull in the API rate-limit increase by two weeks? It would unblock three deals.',
    left:  { label: "I'll see what I can do",       e: -10, x: +10, u:  0, p: -5 },
    right: { label: 'No, the team is at capacity', e:  +5, x:  -5, u:  0, p:  0 },
  },

  // ── Day 60-75 ────────────────────────────────────────────────────────
  {
    id: 'c051', role: 'designer', name: 'Theo', title: 'Product Designer',
    prompt: 'I designed three options for the new nav. One is bold, one is safe, one is for engineers.',
    left:  { label: 'Ship the bold one', e: -5, x: +5, u: +10, p: -5 },
    right: { label: 'Ship the safe one', e: +5, x:  0, u:   0, p:  0 },
  },
  {
    id: 'c052', role: 'engineer', name: 'Priya', title: 'Staff Engineer',
    prompt: 'Tech debt I flagged in week one is now blocking the launch. Postmortem time?',
    left:  { label: 'Yes, full postmortem', e: +10, x:  0, u:  0, p: -10 },
    right: { label: 'No, just keep moving', e:  -5, x: +5, u: -5, p:   0 },
  },
  {
    id: 'c053', role: 'data', name: 'Ash', title: 'Data Scientist',
    prompt: "Our DAU graph has a weird Sunday spike. I think it's bot traffic from the docs site.",
    left:  { label: 'Strip it from the metric', e:  0, x: -5, u: +5, p: -5 },
    right: { label: "Leave it, it's pretty",    e:  0, x:  0, u: -5, p:  0 },
  },
  {
    id: 'c054', role: 'exec', name: 'Tom', title: 'CEO',
    prompt: "Stripe just shipped what we've been building for three quarters. Want me to call them?",
    left:  { label: 'Talk to Patrick',         e:  0, x:  0, u:  0, p:  0 },
    right: { label: "Don't, we have an angle", e: +5, x: +5, u: +5, p: -5 },
  },
  {
    id: 'c055', role: 'customer', name: 'BigCo IT', title: 'Admin',
    prompt: '"We\'ve been on the trial for 90 days. We need a 90-day extension."',
    left:  { label: 'Grant it',         e:  0, x: -5, u: +5, p:  0 },
    right: { label: 'Make them commit', e:  0, x: +5, u: -5, p: -5 },
  },
  {
    id: 'c056', role: 'sales', name: 'Jules', title: 'Enterprise AE',
    prompt: 'Procurement wants a 60% volume discount. The CRO says yes. The model says no.',
    left:  { label: 'Hold the line',     e:  0, x: -10, u:  0, p: -5 },
    right: { label: 'Take the discount', e:  0, x:  +5, u:  0, p:  0 },
  },
  {
    id: 'c057', role: 'engineer', name: 'Marco', title: 'Engineering Manager',
    prompt: "Two of my best engineers want to move to platform team. We'd lose velocity on the roadmap.",
    left:  { label: 'Let them move',      e: +10, x: -5, u:  0, p:  0 },
    right: { label: 'Block the transfer', e: -10, x:  0, u: -5, p: -5 },
  },
  {
    id: 'c058', role: 'designer', name: 'Nina', title: 'UX Researcher',
    prompt: 'Eight of ten users got stuck on the settings page in usability testing. Want the clip reel?',
    left:  { label: 'Send to leadership',  e:  0, x: -5, u: +10, p: -5 },
    right: { label: 'Just fix it quietly', e: +5, x:  0, u:  +5, p:  0 },
  },
  {
    id: 'c059', role: 'intern', name: 'Wren', title: 'Product Intern',
    prompt: '"I noticed the unsubscribe link is broken. I\'m going to fix it before I leave."',
    left:  { label: 'Approve the fix',   e:  0, x:  0, u: +10, p:  0 },
    right: { label: 'Have an FTE do it', e: +5, x:  0, u:   0, p: -5 },
  },
  {
    id: 'c060', role: 'exec', name: 'Karen', title: 'VP Product',
    prompt: 'I need a 30-60-90 from you for the board. Tomorrow.',
    left:  { label: 'Pull an all-nighter',  e:  0, x: +10, u:  0, p: -15 },
    right: { label: "Send last quarter's", e: -5, x:  -5, u:  0, p:   0 },
  },

  // ── Day 75-90 ────────────────────────────────────────────────────────
  {
    id: 'c061', role: 'engineer', name: 'Diego', title: 'Senior Engineer',
    prompt: 'Should I do my own retro deck or use the team template?',
    left:  { label: 'Use the template', e: -5, x: +5, u:  0, p: +5 },
    right: { label: 'Do your own',      e: +5, x: -5, u:  0, p:  0 },
  },
  {
    id: 'c062', role: 'designer', name: 'Rita', title: 'Senior Designer',
    prompt: 'We\'ve been calling our design system "Atlas" but legal says someone else owns the trademark.',
    left:  { label: 'Rename it', e: -5, x:  0, u:  0, p: -5 },
    right: { label: 'Risk it',   e:  0, x:  0, u:  0, p:  0 },
  },
  {
    id: 'c063', role: 'data', name: 'Lin', title: 'Senior Data Scientist',
    prompt: "Marketing asked me to run an A/B test on a feature that's already 100% rolled out.",
    left:  { label: 'Tell them no',           e: +5, x: -5, u:  0, p:  0 },
    right: { label: 'Fake it with a holdout', e: -5, x: +5, u: -5, p: -5 },
  },
  {
    id: 'c064', role: 'customer', name: 'Eve', title: 'Power User, ScaleCo',
    prompt: '"Your competitor offered to migrate us for free. Convince me to stay."',
    left:  { label: 'Get on a call', e:  0, x: +5, u: +10, p: -10 },
    right: { label: 'Send the deck', e:  0, x:  0, u:  -5, p:   0 },
  },
  {
    id: 'c065', role: 'sales', name: 'Gabe', title: 'Account Executive',
    prompt: 'Can you sit in on the QBR? The customer specifically asked for "someone technical".',
    left:  { label: 'Yes',         e:  0, x: +5, u: +5, p: -10 },
    right: { label: 'Send the EM', e: -5, x:  0, u:  0, p:   5 },
  },
  {
    id: 'c066', role: 'exec', name: 'Sandra', title: 'CFO',
    prompt: "Your team's burn is 12% over plan. What's getting cut?",
    left:  { label: 'Cut the AI experiments', e: +5, x: -5, u:  -5, p:  0 },
    right: { label: 'Cut the redesign',       e: -5, x:  0, u: -10, p: -5 },
  },
  {
    id: 'c067', role: 'engineer', name: 'Yusuf', title: 'Frontend Lead',
    prompt: 'We have a chance to deprecate IE11 support. Saves us 200ms on every page.',
    left:  { label: 'Deprecate it',       e: +10, x:  0, u: +5, p:  0 },
    right: { label: 'Wait one more year', e:  -5, x:  0, u: -5, p:  0 },
  },
  {
    id: 'c068', role: 'intern', name: 'Wren', title: 'Product Intern',
    prompt: '"Last day. I made a deck of everything I think you should fix. Want to see it?"',
    left:  { label: 'Read it carefully', e: +5, x:  0, u: +10, p: -5 },
    right: { label: 'Skim it later',     e: -5, x:  0, u:  -5, p:  0 },
  },
  {
    id: 'c069', role: 'recruiter', name: 'Sam', title: 'Tech Recruiter',
    prompt: 'Three people on the eng team want to refer their friends. Should we relax the bar?',
    left:  { label: 'Keep the bar',         e: +5, x:  0, u:  0, p:  0 },
    right: { label: 'Fast-track referrals', e: -5, x: +5, u:  0, p: +5 },
  },
  {
    id: 'c070', role: 'exec', name: 'Marcus', title: 'CRO',
    prompt: 'Sales wants "AI" in the product copy on every page. Can we ship a banner this week?',
    left:  { label: 'Yes, slap it on',  e: -10, x: +10, u: -5, p:  0 },
    right: { label: "No, that's noise", e:  +5, x:  -5, u:  0, p: -5 },
  },

  // ── Final stretch ────────────────────────────────────────────────────
  {
    id: 'c071', role: 'engineer', name: 'Priya', title: 'Staff Engineer',
    prompt: "I'm thinking about leaving. Not for money. The roadmap doesn't excite me.",
    left:  { label: 'Have a real 1:1', e: +10, x:  0, u: +5, p: -10 },
    right: { label: 'Wish them luck',  e: -10, x:  0, u: -5, p:   0 },
  },
  {
    id: 'c072', role: 'designer', name: 'Theo', title: 'Product Designer',
    prompt: 'Marketing wants to use the unreleased v2 screens in a launch video. Pre-launch.',
    left:  { label: "No, it's not done", e: +5, x: -5, u:  0, p:  0 },
    right: { label: 'Sure, why not',     e: -5, x: +5, u:  0, p:  0 },
  },
  {
    id: 'c073', role: 'data', name: 'Ash', title: 'Data Scientist',
    prompt: "I rebuilt the funnel. Real activation is 11%, not 24%. We've been telling investors 24%.",
    left:  { label: 'Disclose tonight', e: +5, x: -10, u: +5, p: -10 },
    right: { label: 'Sit on it',        e: -5, x:  +5, u: -5, p:  -5 },
  },
  {
    id: 'c074', role: 'customer', name: 'BigCo IT', title: 'Procurement',
    prompt: '"Renewal is up. We want a 30% reduction or we\'re going to vendor X."',
    left:  { label: 'Match the discount', e:  0, x:  -5, u: +5, p: -5 },
    right: { label: 'Let them walk',      e:  0, x: -10, u: -5, p:  0 },
  },
  {
    id: 'c075', role: 'sales', name: 'Jules', title: 'Enterprise AE',
    prompt: 'Can you join my prospect dinner Friday? They specifically want "product perspective".',
    left:  { label: 'Go to the dinner', e:  0, x: +5, u: +5, p: -10 },
    right: { label: 'Send a deck',      e:  0, x: -5, u:  0, p:   5 },
  },
  {
    id: 'c076', role: 'engineer', name: 'Marco', title: 'Engineering Manager',
    prompt: 'My team did a blameless retro. Half the items are about your roadmap changes.',
    left:  { label: 'Read it, own it',      e: +10, x:  0, u:  0, p: -10 },
    right: { label: 'Push back on framing', e: -10, x:  0, u:  0, p:  -5 },
  },
  {
    id: 'c077', role: 'exec', name: 'Tom', title: 'CEO',
    prompt: 'I want you to own a new GTM motion. Self-serve. Your call on the team.',
    left:  { label: 'Take it on',   e:  0, x: +15, u: +5, p: -15 },
    right: { label: 'Stay focused', e: +5, x: -10, u:  0, p:   0 },
  },
  {
    id: 'c078', role: 'intern', name: 'Wren', title: 'Returning Intern',
    prompt: '"I\'m back from school. Got a return offer from Meta. Should I stay there or come here?"',
    left:  { label: 'Sell them on us',      e: +5, x:  0, u: +5, p: -5 },
    right: { label: 'Honest pros and cons', e:  0, x:  0, u:  0, p:  0 },
  },
  {
    id: 'c079', role: 'recruiter', name: 'Sam', title: 'Tech Recruiter',
    prompt: 'We have a perfect senior PM lined up. They want fully remote. We\'re "flexible hybrid".',
    left:  { label: 'Make an exception', e:  0, x: -5, u: +5, p:  0 },
    right: { label: 'Hold the policy',   e:  0, x: +5, u: -5, p:  0 },
  },
  {
    id: 'c080', role: 'designer', name: 'Nina', title: 'UX Researcher',
    prompt: 'We have budget for one major research initiative. Onboarding deep-dive or churn interviews?',
    left:  { label: 'Onboarding',       e:  0, x:  0, u: +10, p: -5 },
    right: { label: 'Churn interviews', e: +5, x:  0, u:  +5, p: -5 },
  },
  {
    id: 'c081', role: 'engineer', name: 'Diego', title: 'Senior Engineer',
    prompt: "I'd like to write a public engineering blog post about our migration. Marketing said no.",
    left:  { label: 'Override marketing',  e: +10, x: -10, u:  0, p: -5 },
    right: { label: 'Side with marketing', e: -10, x:  +5, u:  0, p:  0 },
  },
  {
    id: 'c082', role: 'customer', name: 'Eve', title: 'Power User',
    prompt: '"You have a private alpha for the AI feature. Can I get in? I\'ll give you a testimonial."',
    left:  { label: 'Add them',     e: -5, x:  0, u: +10, p:  0 },
    right: { label: 'Wait for GA',  e:  0, x:  0, u:  -5, p:  0 },
  },
  {
    id: 'c083', role: 'exec', name: 'Karen', title: 'VP Product',
    prompt: "I'm leaving in a month. Want my job? I'd back you, but you've been here 80 days.",
    left:  { label: 'Yes',     e: -5, x: +20, u:  0, p: -10 },
    right: { label: 'Not yet', e: +5, x:  -5, u:  0, p:   5 },
  },
  {
    id: 'c084', role: 'data', name: 'Lin', title: 'Senior Data Scientist',
    prompt: 'I want to set up a weekly product-metrics review. Standing 30 min, every Monday. Approved?',
    left:  { label: 'Approved',                  e: +5, x: +5, u: +5, p: -5 },
    right: { label: 'We have too many meetings', e:  0, x: -5, u: -5, p: +5 },
  },
  {
    id: 'c085', role: 'sales', name: 'Gabe', title: 'Account Executive',
    prompt: 'Can I record your demo and send it to leads? We get a 3x reply rate when PMs do them.',
    left:  { label: 'Sure, record it',   e:  0, x: +5, u: +5, p: -5 },
    right: { label: "I'm not Marketing", e:  0, x: -5, u:  0, p:  0 },
  },
  {
    id: 'c086', role: 'engineer', name: 'Yusuf', title: 'Frontend Lead',
    prompt: 'We have to deprecate the old API. Two customers are on it. Both are tiny. Both will scream.',
    left:  { label: 'Deprecate on schedule', e: +5, x:  0, u: -5, p: -5 },
    right: { label: 'Delay six months',      e: -5, x:  0, u: +5, p:  0 },
  },
  {
    id: 'c087', role: 'designer', name: 'Rita', title: 'Senior Designer',
    prompt: 'I want to stop designing in Figma and try Penpot. The team is split.',
    left:  { label: 'Sure, experiment', e:  0, x:  0, u:  0, p: -5 },
    right: { label: 'Stay on Figma',    e:  0, x:  0, u:  0, p:  0 },
  },
  {
    id: 'c088', role: 'exec', name: 'Marcus', title: 'CRO',
    prompt: 'I want a public roadmap. Sales would love it. Eng would hate it.',
    left:  { label: 'Ship a public roadmap', e: -10, x: +10, u: +5, p: -5 },
    right: { label: 'Keep it internal',      e:  +5, x:  -5, u:  0, p:  0 },
  },
  {
    id: 'c089', role: 'customer', name: 'BigCo IT', title: 'CISO',
    prompt: '"You had a minor incident last month. We want a written postmortem and a credit."',
    left:  { label: 'Send both',       e:  0, x: -5, u: +10, p: -5 },
    right: { label: 'Postmortem only', e:  0, x:  0, u:  +5, p:  0 },
  },
  {
    id: 'c090', role: 'intern', name: 'Wren', title: 'Returning Intern',
    prompt: '"For my final presentation: what\'s the one thing you wish someone told you on day one?"',
    left:  { label: 'Be honest',          e: +5, x:  0, u: +5, p: -5 },
    right: { label: 'Say something safe', e:  0, x:  0, u:  0, p:  0 },
  },

  // ── Day 90+: extended season ─────────────────────────────────────────
  {
    id: 'c091', role: 'engineer', name: 'Yusuf', title: 'Frontend Lead',
    prompt: "Eng wants to migrate from Jira to Linear. Half the team has muscle memory in Jira queries.",
    left:  { label: 'Approve the move', e: +5, x: -5, u:  0, p: -5 },
    right: { label: 'Stay on Jira',     e: -5, x: +5, u:  0, p:  0 },
  },
  {
    id: 'c092', role: 'designer', name: 'Theo', title: 'Product Designer',
    prompt: 'Eng wants Storybook for every component. Three weeks of work, no user-facing change.',
    left:  { label: 'Make the time',   e: +10, x: -5, u:  0, p: -5 },
    right: { label: 'Skip it for now', e:  -5, x: +5, u:  0, p:  0 },
  },
  {
    id: 'c093', role: 'exec', name: 'Karen', title: 'VP Product',
    prompt: 'My board deck is 47 slides. Help me cut it before Thursday?',
    left:  { label: 'Cut it to 12',    e:  0, x: +10, u:  0, p: -10 },
    right: { label: 'Add an appendix', e:  0, x:   0, u:  0, p:  -5 },
  },
  {
    id: 'c094', role: 'customer', name: 'Eve', title: 'Power User',
    prompt: '"Why is your support on Intercom and not Slack? We live in Slack."',
    left:  { label: 'Add a Slack channel', e: -5, x:  0, u: +10, p: -5 },
    right: { label: 'Stick with Intercom', e:  0, x:  0, u:  -5, p:  0 },
  },
  {
    id: 'c095', role: 'sales', name: 'Jules', title: 'Enterprise AE',
    prompt: 'Procurement wants unlimited indemnity in the contract. Legal said never. Sales says yes.',
    left:  { label: 'Cap at $1M',       e:  0, x:  +5, u:  0, p: -5 },
    right: { label: 'Accept unlimited', e: -5, x: +10, u:  0, p: -10,
             triggers: 'f_indemnity' },
  },
  {
    id: 'c096', role: 'data', name: 'Lin', title: 'Senior Data Scientist',
    prompt: 'We have 47 dashboards. Eight people use them. Want me to deprecate the bottom 30?',
    left:  { label: 'Deprecate them',   e: +5, x:  0, u:  0, p: +5 },
    right: { label: 'Leave them alone', e: -5, x:  0, u:  0, p:  0 },
  },
  {
    id: 'c097', role: 'engineer', name: 'Marco', title: 'Engineering Manager',
    prompt: "We need a 24/7 on-call rotation. Three engineers refuse. Two say they'll quit if forced.",
    left:  { label: 'Make it voluntary', e: +10, x: -10, u: -5, p: -5,
             triggers: 'f_oncall' },
    right: { label: 'Mandate it',        e: -10, x:  +5, u:  0, p:  0 },
  },
  {
    id: 'c098', role: 'designer', name: 'Rita', title: 'Senior Designer',
    prompt: 'Brand wants a 60-page guideline doc. Eng says nobody will read it.',
    left:  { label: 'Build the full doc',  e: -5, x: +5, u:  0, p: -10,
             triggers: 'f_brand' },
    right: { label: 'A one-pager will do', e: +5, x: -5, u:  0, p:  +5 },
  },
  {
    id: 'c099', role: 'intern', name: 'Wren', title: 'Returning Intern',
    prompt: '"I want a mentor outside my chain. Can you find me one?"',
    left:  { label: 'Set them up',      e:  0, x:  0, u: +5, p: -5 },
    right: { label: 'Suggest they DIY', e:  0, x:  0, u:  0, p:  0 },
  },
  {
    id: 'c100', role: 'exec', name: 'Marcus', title: 'CRO',
    prompt: "Buy CompetitorCo's analytics module for $400k or build it ourselves in two quarters?",
    left:  { label: 'Buy it',             e: -5, x: +10, u: +5, p:  0,
             triggers: 'f_buy_build' },
    right: { label: 'Build it ourselves', e: +5, x:  -5, u:  0, p: -10 },
  },
  {
    id: 'c101', role: 'customer', name: 'BigCo IT', title: 'Admin',
    prompt: 'NPS dropped 14 points in one month. The verbatims all mention "performance".',
    left:  { label: 'Pause features for perf',   e: +10, x: -5, u: +10, p: -10 },
    right: { label: 'Push perf to next quarter', e:  -5, x: +5, u: -10, p:   0 },
  },
  {
    id: 'c102', role: 'recruiter', name: 'Sam', title: 'Tech Recruiter',
    prompt: 'We benchmarked our PM comp band. We are 18% below market. HR refuses to adjust.',
    left:  { label: 'Escalate to CEO', e:  0, x: -10, u:  0, p: -5 },
    right: { label: 'Live with it',    e: -5, x:   0, u:  0, p:  0 },
  },
  {
    id: 'c103', role: 'engineer', name: 'Diego', title: 'Senior Engineer',
    prompt: "I want to split the monorepo. It's eight months of work. The team is split.",
    left:  { label: 'Approve the split', e: +5, x: -5, u: -5, p: -10,
             triggers: 'f_monorepo' },
    right: { label: 'Keep the monorepo', e: -5, x:  0, u:  0, p:  0 },
  },
  {
    id: 'c104', role: 'sales', name: 'Gabe', title: 'Account Executive',
    prompt: 'A reseller wants to white-label us in APAC. 30% margin to them, no GTM cost to us.',
    left:  { label: 'Sign the deal', e:  0, x: +10, u:  0, p: -5,
             triggers: 'f_channel' },
    right: { label: 'Stay direct',   e:  0, x:  -5, u:  0, p:  0 },
  },
  {
    id: 'c105', role: 'exec', name: 'Tom', title: 'CEO',
    prompt: 'I want to hire a Head of AI. The candidate is from OpenAI and wants $700k base.',
    left:  { label: 'Make the offer',     e:  0, x: +10, u: +5, p: -10,
             triggers: 'f_ai_hire' },
    right: { label: 'Promote internally', e: +5, x:  -5, u:  0, p:   0 },
  },
  {
    id: 'c106', role: 'data', name: 'Ash', title: 'Data Scientist',
    prompt: 'GDPR audit found four cookies we set without consent. Legal wants them gone tonight.',
    left:  { label: 'Pull them tonight',   e: -5, x: +5, u: +10, p: -10 },
    right: { label: 'Schedule for sprint', e:  0, x: -5, u: -10, p:   0 },
  },
  {
    id: 'c107', role: 'designer', name: 'Nina', title: 'UX Researcher',
    prompt: 'Accessibility audit found 38 WCAG-AA failures. Most are color contrast.',
    left:  { label: 'Block release until fixed',  e: +10, x: -5, u: +10, p: -10,
             triggers: 'f_a11y' },
    right: { label: 'Ship, file backlog tickets', e:  -5, x: +5, u:  -5, p:   0 },
  },
  {
    id: 'c108', role: 'customer', name: 'Eve', title: 'Power User, ScaleCo',
    prompt: '"Your status page said \'all systems operational\' during a 90-minute outage. We notice."',
    left:  { label: 'Postmortem + apology',   e:  0, x:  0, u: +10, p: -5 },
    right: { label: 'Update the SLA quietly', e:  0, x: -5, u:  -5, p:  0 },
  },
  {
    id: 'c109', role: 'sales', name: 'Jules', title: 'Enterprise AE',
    prompt: 'I have three RFPs due Friday. Each is 80 pages of compliance questions.',
    left:  { label: 'Help with all three', e:  0, x: +5, u:  0, p: -15,
             triggers: 'f_rfp' },
    right: { label: 'Triage to one',       e:  0, x: -5, u:  0, p:  -5 },
  },
  {
    id: 'c110', role: 'exec', name: 'Sandra', title: 'CFO',
    prompt: "We're 18 months from default. Layoffs or cut the next product line?",
    left:  { label: 'Cut the product line', e: -10, x: +5, u: -5, p:   0,
             triggers: 'f_layoffs' },
    right: { label: 'Smaller layoffs',      e:   0, x:  0, u: -5, p: -10 },
  },

  // ── Follow-up cards (only surfaced via triggers) ─────────────────────
  {
    id: 'f_custom_arr', followup: true, role: 'sales', name: 'Jules', title: 'Enterprise AE',
    prompt: 'Three weeks later. The €500k custom feature shipped. They want two more.',
    left:  { label: 'Hold the line',      e:  +5, x: -10, u:   0, p:   0 },
    right: { label: 'Build the next one', e: -10, x:  +5, u: -10, p: -10 },
  },
  {
    id: 'f_salesforce_sync', followup: true, role: 'engineer', name: 'Marco', title: 'Engineering Manager',
    prompt: "About that Salesforce sync. It's three sprints of work. We don't have three sprints.",
    left:  { label: 'Cut a different feature',          e:  0, x:  -5, u: -10, p: -5 },
    right: { label: "Tell sales it's not happening",    e: +5, x: -10, u:   0, p:  0 },
  },
  {
    id: 'f_indemnity', followup: true, role: 'sales', name: 'Jules', title: 'Enterprise AE',
    prompt: 'Three weeks later. The customer triggered a $3M claim under that unlimited indemnity.',
    left:  { label: 'Pay it, learn',     e:  0, x: -10, u:  0, p: -10 },
    right: { label: 'Fight it in court', e:  0, x:  -5, u: -5, p: -10 },
  },
  {
    id: 'f_oncall', followup: true, role: 'engineer', name: 'Marco', title: 'Engineering Manager',
    prompt: "Two weeks in. Voluntary on-call had one volunteer. He's burning out fast.",
    left:  { label: 'Pay extra, double rotation', e:  +5, x: -5, u: +5, p: -10 },
    right: { label: 'Mandate it now',             e: -10, x: +5, u:  0, p:  -5 },
  },
  {
    id: 'f_brand', followup: true, role: 'designer', name: 'Rita', title: 'Senior Designer',
    prompt: 'The 60-page brand doc is done. Engineering is using none of it.',
    left:  { label: 'Mandate compliance',  e: -10, x:  0, u:  0, p: -5 },
    right: { label: 'Let it die quietly',  e:  +5, x: -5, u:  0, p:  0 },
  },
  {
    id: 'f_buy_build', followup: true, role: 'exec', name: 'Marcus', title: 'CRO',
    prompt: "The CompetitorCo module won't integrate. Their API is undocumented.",
    left:  { label: 'Demand a refund', e:  0, x: -10, u: -5, p:  -5 },
    right: { label: 'Build a wrapper', e: -5, x:   0, u:  0, p: -10 },
  },
  {
    id: 'f_monorepo', followup: true, role: 'engineer', name: 'Diego', title: 'Senior Engineer',
    prompt: 'Three months in. The split is half-done. CI is broken in both repos.',
    left:  { label: 'Roll it back', e: -10, x:  0, u:  0, p: -10 },
    right: { label: 'Push through', e:  +5, x: -5, u: -5, p: -10 },
  },
  {
    id: 'f_channel', followup: true, role: 'sales', name: 'Gabe', title: 'Account Executive',
    prompt: 'The APAC reseller is undercutting our direct deals in ANZ. Customers are confused.',
    left:  { label: 'Carve out ANZ',    e:  0, x:  -5, u: +5, p: -5 },
    right: { label: 'Cut the reseller', e:  0, x: -10, u:  0, p:  0 },
  },
  {
    id: 'f_ai_hire', followup: true, role: 'exec', name: 'Tom', title: 'CEO',
    prompt: 'The Head of AI started Monday. By Friday he wants to redirect the whole roadmap.',
    left:  { label: 'Hear him out',  e: -5, x:  +5, u:  0, p: -10 },
    right: { label: 'Hold the line', e: +5, x: -10, u:  0, p:   0 },
  },
  {
    id: 'f_a11y', followup: true, role: 'designer', name: 'Nina', title: 'UX Researcher',
    prompt: "All 38 WCAG fixes shipped. Sales says we're now eligible for a public-sector RFP.",
    left:  { label: 'Pursue the RFP',      e:  0, x: +10, u: +5, p: -5 },
    right: { label: 'Stay focused on SMB', e:  0, x:   0, u:  0, p:  0 },
  },
  {
    id: 'f_rfp', followup: true, role: 'sales', name: 'Jules', title: 'Enterprise AE',
    prompt: 'We won one of the three RFPs. The committed delivery is 6 weeks. Eng laughed.',
    left:  { label: 'Renegotiate scope',     e:  +5, x: -5, u:  0, p:  -5 },
    right: { label: 'Promise, then stretch', e: -10, x: +5, u: -5, p: -10 },
  },
  {
    id: 'f_layoffs', followup: true, role: 'exec', name: 'Sandra', title: 'CFO',
    prompt: 'The product line you cut had three customers paying $1.2M ARR. They want refunds.',
    left:  { label: 'Refund in full',                  e:  0, x: -10, u: +5, p: -5 },
    right: { label: 'Offer credit on the core product', e: 0, x:   0, u: -5, p:  0 },
  },

  // ── Day 90+: third season ────────────────────────────────────────────
  {
    id: 'c111', role: 'exec', name: 'Hugo', title: 'CTO',
    prompt: 'I want to bring everything in-house. Drop AWS, drop Stripe, build it ourselves. Three-year plan.',
    left:  { label: 'Run a feasibility study',       e:  +5, x: -5,  u:  0, p: -10,
             triggers: 'f_in_house' },
    right: { label: 'Stay focused on what we have',  e:   0, x: -5,  u:  0, p:   0 },
  },
  {
    id: 'c112', role: 'engineer', name: 'Petra', title: 'Mobile Lead',
    prompt: "Apple just rejected our app. They flagged the AI feature as 'unverified information generation'.",
    left:  { label: 'Resubmit with disclaimers', e:  0, x:   0, u: -5, p: -5,
             triggers: 'f_apple_reject' },
    right: { label: 'Fight the rejection',       e: -5, x: -10, u:  0, p: -5 },
  },
  {
    id: 'c113', role: 'exec', name: 'Karen', title: 'VP Product',
    prompt: 'I want quarterly planning to take five days, not two. Real OKRs, not slides.',
    left:  { label: 'Embrace it',          e: +5, x: +5, u:  0, p: -10 },
    right: { label: 'Two days is enough',  e: -5, x: -5, u:  0, p:  +5 },
  },
  {
    id: 'c114', role: 'customer', name: 'Eve', title: 'Power User',
    prompt: '"We want to be a design partner. Free product, you get our logo and a quote."',
    left:  { label: 'Sign them up',         e:  0, x: +5, u: +5, p: -5,
             triggers: 'f_design_partner' },
    right: { label: "We don't need a logo", e:  0, x:  0, u:  0, p:  0 },
  },
  {
    id: 'c115', role: 'engineer', name: 'Anand', title: 'Security Engineer',
    prompt: 'Pen test found three RCE vulns in the API. We have 24 hours before disclosure.',
    left:  { label: 'All hands, patch tonight',         e: +10, x: -5, u:  +5, p: -15,
             triggers: 'f_pen_test' },
    right: { label: 'Negotiate the disclosure window',  e:  -5, x:  0, u:  -5, p:   0 },
  },
  {
    id: 'c116', role: 'sales', name: 'Mei', title: 'Sales Engineer',
    prompt: "The prospect's RFP requires GDPR + CCPA + LGPD + PIPEDA compliance. We have GDPR.",
    left:  { label: 'Commit to all four',     e: -10, x:  +5, u:  0, p: -10 },
    right: { label: 'Disqualify ourselves',   e:  +5, x: -10, u:  0, p:   0 },
  },
  {
    id: 'c117', role: 'data', name: 'Lin', title: 'Senior Data Scientist',
    prompt: 'Marketing wants to track every page event in Mixpanel. The tracking script is 240KB.',
    left:  { label: 'Trim to events that matter', e: +5, x:  0, u: +5, p:  0 },
    right: { label: 'Track everything, sort later', e: -5, x: +5, u: -5, p:  0 },
  },
  {
    id: 'c118', role: 'designer', name: 'Theo', title: 'Product Designer',
    prompt: 'Brand wants a complete refresh. Logo, palette, typography. Six months of work.',
    left:  { label: 'Champion the project',          e:  0, x: +5, u: +5, p: -10,
             triggers: 'f_brand_refresh' },
    right: { label: 'Push back, focus on product',   e: +5, x: -5, u:  0, p:   0 },
  },
  {
    id: 'c119', role: 'exec', name: 'Tom', title: 'CEO',
    prompt: 'I want to acquire ToolCo. They have 2k customers, $4M ARR, no growth. $20M deal.',
    left:  { label: 'Run diligence', e:  0, x: +10, u: +5, p: -10,
             triggers: 'f_acquisition' },
    right: { label: 'Pass on it',    e: +5, x:  -5, u:  0, p:   0 },
  },
  {
    id: 'c120', role: 'recruiter', name: 'Sam', title: 'Tech Recruiter',
    prompt: 'Three of our PMs got LinkedIn-poached this week. Three different competitors.',
    left:  { label: 'Investigate culture', e: +5, x:  0, u:  0, p: -5 },
    right: { label: 'Wait it out',         e: -5, x:  0, u: -5, p:  0 },
  },
  {
    id: 'c121', role: 'exec', name: 'Marcus', title: 'CRO',
    prompt: 'I want every PM in sales calls weekly. Mandatory.',
    left:  { label: 'Try it',           e: -5, x: +5, u: +5, p: -10 },
    right: { label: 'Bi-weekly, opt-in', e: +5, x: -5, u:  0, p:  +5 },
  },
  {
    id: 'c122', role: 'engineer', name: 'Diego', title: 'Senior Engineer',
    prompt: 'Our docs site has 60% bounce rate. Want me to spend a sprint rewriting?',
    left:  { label: 'Approve the sprint',  e: +5, x:  0, u: +10, p: -5 },
    right: { label: 'Hire a tech writer',  e:  0, x: -5, u:  +5, p: +5 },
  },
  {
    id: 'c123', role: 'customer', name: 'BigCo IT', title: 'Procurement',
    prompt: '"We want our logo OFF your homepage. Compliance reasons."',
    left:  { label: 'Remove tonight',          e:  0, x:  0, u:  0, p: -5 },
    right: { label: 'Push back, sales asset',  e:  0, x: +5, u: -5, p:  0 },
  },
  {
    id: 'c124', role: 'data', name: 'Ash', title: 'Data Scientist',
    prompt: 'I want to publish a paper on our churn model. Co-author with academics.',
    left:  { label: 'Approve',         e: +10, x:  0, u:  0, p: -5 },
    right: { label: 'Stay heads-down', e: -10, x: +5, u:  0, p:  0 },
  },
  {
    id: 'c125', role: 'intern', name: 'Wren', title: 'Returning Intern',
    prompt: '"I\'m publishing a Medium post about my internship. Want to review it?"',
    left:  { label: 'Read carefully', e:  0, x:  0, u: +5, p: -5 },
    right: { label: 'Trust them',     e: +5, x:  0, u:  0, p:  0 },
  },
  {
    id: 'c126', role: 'exec', name: 'Beatrice', title: 'Chief of Staff',
    prompt: "The CEO wants you to draft his board update. He'll review tomorrow.",
    left:  { label: 'Take it on',       e:  0, x: +10, u:  0, p: -10 },
    right: { label: 'Decline politely', e:  0, x: -10, u:  0, p:  +5 },
  },
  {
    id: 'c127', role: 'designer', name: 'Nina', title: 'UX Researcher',
    prompt: 'I want to interview your worst customers. The ones who churned angry.',
    left:  { label: 'Approve the budget',    e:  0, x: -5, u: +10, p: -5 },
    right: { label: 'Stick to current users', e:  0, x:  0, u:  -5, p:  0 },
  },
  {
    id: 'c128', role: 'engineer', name: 'Yusuf', title: 'Frontend Lead',
    prompt: "We have a 4MB JS bundle. Sales says product feels 'slow'.",
    left:  { label: 'Run a perf sprint', e: +10, x:  0, u: +10, p: -10,
             triggers: 'f_perf_sprint' },
    right: { label: 'Live with it',      e: -10, x:  0, u:  -5, p:   0 },
  },
  {
    id: 'c129', role: 'sales', name: 'Jules', title: 'Enterprise AE',
    prompt: 'Customer wants a shared private Slack channel with the eng team.',
    left:  { label: 'Open the channel', e: -5, x: +5, u: +5, p: -5,
             triggers: 'f_private_slack' },
    right: { label: 'Use Intercom',     e: +5, x: -5, u: -5, p:  0 },
  },
  {
    id: 'c130', role: 'exec', name: 'Sandra', title: 'CFO',
    prompt: 'Our cloud costs spike on Black Friday. Reserved instances or autoscale?',
    left:  { label: 'Reserved capacity',     e:  0, x: +5, u:  0, p: -5 },
    right: { label: 'Autoscale, eat the cost', e: 0, x: -5, u:  0, p:  0 },
  },
  {
    id: 'c131', role: 'engineer', name: 'Priya', title: 'Staff Engineer',
    prompt: 'I rewrote the rate-limiter on a Saturday. PR is open.',
    left:  { label: 'Review and merge',           e:  +5, x:  0, u: +5, p: -5 },
    right: { label: "Don't normalize weekend work", e: +10, x: -5, u:  0, p: +5 },
  },
  {
    id: 'c132', role: 'designer', name: 'Rita', title: 'Senior Designer',
    prompt: "I'm doing the WWDC talk on accessibility. Time off?",
    left:  { label: 'Approve, full support',  e: +5, x:  0, u: +5, p: -5 },
    right: { label: 'Maybe a smaller venue',  e: -5, x:  0, u:  0, p:  0 },
  },
  {
    id: 'c133', role: 'recruiter', name: 'Pia', title: 'Head of Talent',
    prompt: 'We have a senior PM offer out. They want to negotiate the stock vesting cliff.',
    left:  { label: 'Renegotiate',     e:  0, x: -5, u:  0, p: -5 },
    right: { label: 'Hold the line',   e: -5, x: +5, u:  0, p:  0 },
  },
  {
    id: 'c134', role: 'engineer', name: 'Marco', title: 'Engineering Manager',
    prompt: 'I want to go from quarterly to monthly performance reviews. Calibration becomes harder.',
    left:  { label: 'Try it',          e:  0, x:  0, u:  0, p: -10 },
    right: { label: 'Stay quarterly',  e:  0, x:  0, u:  0, p:   0 },
  },
  {
    id: 'c135', role: 'customer', name: 'BigCo IT', title: 'Legal',
    prompt: 'A Russia-based engineer touched our data through our subprocessor.',
    left:  { label: 'Investigate immediately', e: -5, x:   0, u: +5, p: -10,
             triggers: 'f_data_breach' },
    right: { label: 'Cut the subprocessor',    e: -10, x: +5, u:  0, p:  -5 },
  },
  {
    id: 'c136', role: 'customer', name: 'Oliver', title: 'CTO at TenantCo',
    prompt: '"We want a private cloud deployment. Air-gapped. We\'ll pay 5x."',
    left:  { label: 'Build the offering', e: -10, x: +10, u: +5, p: -10 },
    right: { label: 'Pass, not our model', e: +5, x:  -5, u:  0, p:   0 },
  },
  {
    id: 'c137', role: 'sales', name: 'Gabe', title: 'Account Executive',
    prompt: "I made a deck saying we're 'AI-first'. We're not. Sales is using it.",
    left:  { label: 'Correct it now', e:  +5, x: -5, u: +5, p: -5 },
    right: { label: 'Let it ride',    e: -10, x: +5, u: -5, p:  0 },
  },
  {
    id: 'c138', role: 'designer', name: 'Theo', title: 'Product Designer',
    prompt: 'Eng deleted my design tokens config in a refactor. Three weeks lost.',
    left:  { label: 'Full postmortem',          e: -5, x:  0, u:  0, p: -10 },
    right: { label: 'Move on, restore from git', e: +5, x:  0, u:  0, p:   0 },
  },
  {
    id: 'c139', role: 'exec', name: 'Tom', title: 'CEO',
    prompt: "I told Bloomberg we'd ship the AI feature by year-end. It's October.",
    left:  { label: 'Move heaven',  e: -10, x: +10, u: +5, p: -15 },
    right: { label: 'Walk it back', e:  +5, x: -10, u:  0, p:   0 },
  },
  {
    id: 'c140', role: 'exec', name: 'Karen', title: 'VP Product',
    prompt: "I'm taking a sabbatical. Want to be acting VP for three months?",
    left:  { label: 'Yes', e:  0, x: +15, u:  0, p: -15,
             triggers: 'f_acting_vp' },
    right: { label: 'No',  e:  0, x: -10, u:  0, p: +10 },
  },

  // ── Follow-ups ──────────────────────────────────────────────────────
  {
    id: 'f_in_house', followup: true, role: 'exec', name: 'Hugo', title: 'CTO',
    prompt: "Six months in. We're behind on the in-house build. AWS bill hasn't dropped.",
    left:  { label: 'Pull the plug', e: -5, x: -10, u:  0, p:  -5 },
    right: { label: 'Push through',  e: -5, x:   0, u: -5, p: -10 },
  },
  {
    id: 'f_apple_reject', followup: true, role: 'engineer', name: 'Petra', title: 'Mobile Lead',
    prompt: 'Apple accepted the resubmission. Google rejected it the same day.',
    left:  { label: 'Negotiate Google', e:  0, x:  0, u:   0, p: -5 },
    right: { label: 'Ship Apple-only',  e: -5, x: -5, u: -10, p:  0 },
  },
  {
    id: 'f_design_partner', followup: true, role: 'customer', name: 'Eve', title: 'Power User',
    prompt: '"The design partner is now demanding a roadmap seat."',
    left:  { label: 'Give them the seat', e: -5, x:  0, u: +5, p: -5 },
    right: { label: 'Politely decline',   e:  0, x:  0, u: -5, p:  0 },
  },
  {
    id: 'f_pen_test', followup: true, role: 'engineer', name: 'Anand', title: 'Security Engineer',
    prompt: 'We patched the RCEs. The researcher published anyway.',
    left:  { label: 'Coordinate disclosure', e:   0, x:  0, u: +5, p:  -5 },
    right: { label: 'Sue for breach',        e: -10, x:  0, u: -5, p: -10 },
  },
  {
    id: 'f_brand_refresh', followup: true, role: 'designer', name: 'Theo', title: 'Product Designer',
    prompt: 'Brand refresh shipped. Twitter mocked the new logo.',
    left:  { label: 'Defend it publicly', e:  0, x: -5, u:  0, p: -5 },
    right: { label: 'Iterate to v1.5',    e:  0, x: -5, u: -5, p: -5 },
  },
  {
    id: 'f_acquisition', followup: true, role: 'exec', name: 'Tom', title: 'CEO',
    prompt: "ToolCo's CEO wants a board seat as part of the deal. Founders are split.",
    left:  { label: 'Give the seat',     e:  0, x:  0, u:  0, p: -5 },
    right: { label: 'Walk from the deal', e: 0, x: -5, u:  0, p: +5 },
  },
  {
    id: 'f_perf_sprint', followup: true, role: 'engineer', name: 'Yusuf', title: 'Frontend Lead',
    prompt: 'Bundle is 1.2MB. Some features broke.',
    left:  { label: 'Roll back broken features', e: -5, x:  0, u: -5, p:  -5 },
    right: { label: 'Hotfix forward',            e: +5, x:  0, u: +5, p: -10 },
  },
  {
    id: 'f_private_slack', followup: true, role: 'sales', name: 'Jules', title: 'Enterprise AE',
    prompt: 'The customer-eng Slack now has six other PMs lurking.',
    left:  { label: 'Lock it down', e: +5, x:  0, u: -5, p:  0 },
    right: { label: 'Let it grow',  e: -5, x:  0, u:  0, p: -5 },
  },
  {
    id: 'f_data_breach', followup: true, role: 'customer', name: 'BigCo IT', title: 'Legal',
    prompt: "Forensics says one customer's data was accessed. They're asking now.",
    left:  { label: 'Disclose tonight', e:  0, x: -10, u: +10, p: -10 },
    right: { label: 'Wait for legal',   e: -5, x:   0, u: -10, p:  -5 },
  },
  {
    id: 'f_acting_vp', followup: true, role: 'exec', name: 'Karen', title: 'VP Product',
    prompt: "Karen's gone. The team is testing limits.",
    left:  { label: 'Set the bar high',  e: +5, x: +5, u:  0, p: -10 },
    right: { label: 'Earn trust slowly', e:  0, x: -5, u:  0, p:   0 },
  },
];
